import { Injectable, signal, computed } from '@angular/core';

export interface GuideStep {
  content: string;
  targetKeyword?: string;
}

@Injectable({
  providedIn: 'root'
})
export class GuideService {
  isActive = signal<boolean>(false);
  steps = signal<GuideStep[]>([]);
  currentStepIndex = signal<number>(0);

  currentStep = computed(() => {
    const steps = this.steps();
    const index = this.currentStepIndex();
    return steps[index] || null;
  });

  targetRect = signal<DOMRect | null>(null);
  targetElement = signal<HTMLElement | null>(null);

  startGuide(rawSteps: string[]) {
    const parsedSteps = rawSteps.map(s => this.parseStep(s));
    this.steps.set(parsedSteps);
    this.currentStepIndex.set(0);
    this.isActive.set(true);
    this.locateTarget();
  }

  nextStep() {
    if (this.currentStepIndex() < this.steps().length - 1) {
      this.currentStepIndex.update(i => i + 1);
      this.locateTarget();
    } else {
      this.stopGuide();
    }
  }

  prevStep() {
    if (this.currentStepIndex() > 0) {
      this.currentStepIndex.update(i => i - 1);
      this.locateTarget();
    }
  }

  stopGuide() {
    this.isActive.set(false);
    this.steps.set([]);
    this.currentStepIndex.set(0);
    this.targetRect.set(null);
    this.targetElement.set(null);
  }

  private parseStep(content: string): GuideStep {
    // Extract potential keywords from markdown bold/italic or quotes
    const boldRegex = /\*\*(.*?)\*\*/;
    const quoteRegex = /"([^"]*)"|'([^']*)'/;
    
    let keyword: string | undefined;
    
    const boldMatch = content.match(boldRegex);
    if (boldMatch) {
      keyword = boldMatch[1];
    } else {
      const quoteMatch = content.match(quoteRegex);
      if (quoteMatch) {
        keyword = quoteMatch[1] || quoteMatch[2];
      }
    }

    return {
      content: content,
      targetKeyword: keyword
    };
  }

  private locateTarget() {
    const step = this.currentStep();
    if (!step || !step.targetKeyword) {
        this.targetRect.set(null);
        this.targetElement.set(null);
        return;
    }

    const keyword = step.targetKeyword.toLowerCase();
    
    // Heuristic search for elements
    // We prioritize specific interactive elements
    const updateTarget = () => {
        const potentialTargets = Array.from(document.querySelectorAll('button, a, input, select, textarea, [role="button"], span, div, li'));
        
        // precise match first
        let found = potentialTargets.find(el => {
            return (el as HTMLElement).innerText?.toLowerCase().trim() === keyword || 
                   (el as HTMLElement).getAttribute('aria-label')?.toLowerCase() === keyword ||
                   (el as HTMLElement).title?.toLowerCase() === keyword;
        });

        // partial match if no precise match
        if (!found) {
             found = potentialTargets.find(el => {
                const text = (el as HTMLElement).innerText?.toLowerCase() || '';
                return text.includes(keyword) && text.length < keyword.length + 20; // avoid matching huge blocks
            });
        }

        if (found) {
            const rect = found.getBoundingClientRect();
             // Check if visible
            if (rect.width > 0 && rect.height > 0) {
                this.targetRect.set(rect);
                this.targetElement.set(found as HTMLElement);
                found.scrollIntoView({ behavior: 'smooth', block: 'center' });
                return;
            }
        }
        
        this.targetRect.set(null);
        this.targetElement.set(null);
    };

    // Use setTimeout to allow DOM to settle if we just navigated
    setTimeout(updateTarget, 100); 
    setTimeout(updateTarget, 500); 
  }

  constructor() {
    window.addEventListener('scroll', () => {
      if (this.isActive()) this.locateTarget();
    }, { capture: true });
    
    // Better: use capture to detect scroll on any element
    window.addEventListener('scroll', () => {
        if (this.isActive()) this.locateTarget();
    }, true);

    window.addEventListener('resize', () => {
      if (this.isActive()) this.locateTarget();
    });
  }
}
